import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { safePrintHTML } from '@/utils/safePrint';
import { CuttingThemeProvider, useCuttingTheme } from '@/hooks/useCuttingTheme';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Scissors, Sun, Moon, Trash2, AlertTriangle, BarChart3, Table, 
  Sliders, Settings, Calendar, Upload, ClipboardPaste, X, FileSpreadsheet,
  Clock, Gauge, Target, CheckCircle, Timer, TrendingUp, TrendingDown, Download, FileText,
  Bell, Coffee, Utensils, AlertCircle, Filter, Share2, Printer, Archive, Users, Eye, Zap, Check,
  Plus, RefreshCw, Play, Pause
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, ComposedChart, Bar
} from 'recharts';

// Extracted Components
import {
  EmptyState,
  AlertsNotifications,
  OperatingHours,
  KPICards,
  TimeDistribution,
  CompositionChart,
  MonthlyEvolution,
  GoalsManagement,
  SettingsPanel,
  Simulator,
  DailyAnalysisView,
  DayDetail,
  ImportsHistory,
  AnomalousRecordsPanel
} from './cutting-simulator/components';

const API_BASE = '/cutting-simulator';

const DEFAULT_SETTINGS = {
  efficiencyTarget: 35,
  machineStoppedTarget: 50,
  entreCorteTarget: 2,
  speedTarget: 10,
  morningStart: "08:00",
  morningEnd: "12:00",
  afternoonStart: "13:30",
  afternoonEnd: "17:30",
  break1Start: "10:00",
  break2Start: "16:00",
  breakDuration: 10,
  allowWeekends: false, // Permitir trabalho aos fins de semana
};

function CuttingSimulatorContent() {
  const { mode, toggle, t } = useCuttingTheme();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayDetail, setDayDetail] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashboardPeriod, setDashboardPeriod] = useState('all'); // 'today', 'week', 'month', 'custom', 'all'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showDateFilters, setShowDateFilters] = useState(false);
  // Real vs Ajustado — applies operator anomaly windows + record justifications on the backend.
  const [adjusted, setAdjusted] = useState(() => {
    try { return localStorage.getItem('cutting:adjusted') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('cutting:adjusted', adjusted ? '1' : '0'); } catch (_) {}
  }, [adjusted]);
  const [settings, setSettings] = useState(() => {
    try {
      const saved = localStorage.getItem("cutting-settings");
      return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SETTINGS;
    } catch {
      return DEFAULT_SETTINGS;
    }
  });
  
  // Paste data state
  const [pasteStartDate, setPasteStartDate] = useState('');
  const [pasteEndDate, setPasteEndDate] = useState('');
  const [pasteText, setPasteText] = useState('');
  const [pasting, setPasting] = useState(false);
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [detectedDays, setDetectedDays] = useState(0);
  const [parsedLines, setParsedLines] = useState(0);
  const [existingDatesInRange, setExistingDatesInRange] = useState([]);
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);
  const [showDayMismatchModal, setShowDayMismatchModal] = useState(false);
  const [daysToRemove, setDaysToRemove] = useState([]);
  const [detectedBlocks, setDetectedBlocks] = useState([]); // Blocks of operations per day
  const [manualSplits, setManualSplits] = useState([]); // Line indices where user manually split
  
  // Break review modal state
  const [showBreakReviewModal, setShowBreakReviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [breakDeductions, setBreakDeductions] = useState([]);
  const [previewReplace, setPreviewReplace] = useState(false);
  
  // Anomalous records review modal state (Step 2 of import)
  const [showAnomalousReviewModal, setShowAnomalousReviewModal] = useState(false);
  const [anomalousRecords, setAnomalousRecords] = useState([]);
  const [anomalousClassifications, setAnomalousClassifications] = useState({});
  const [pendingImportData, setPendingImportData] = useState(null); // Store data waiting for anomalous classification
  
  // Goals management state
  const [goals, setGoals] = useState([]);
  const [loadingGoals, setLoadingGoals] = useState(false);
  const [showCreateGoalModal, setShowCreateGoalModal] = useState(false);
  const [newGoal, setNewGoal] = useState({
    name: '',
    periodStart: '',
    periodEnd: '',
    betweenMarkersReduction: 20,
    machineStoppedReduction: 15,
    // New target fields
    targetBetweenMarkersTime: 2.0,  // Target time in minutes (e.g., ≤2min)
    targetEfficiency: 35,            // Target efficiency % (e.g., ≥35%)
    targetMachineStoppedPercent: 10, // Target % machine stopped relative to cutting (e.g., ≤10%)
  });
  const [creatingGoal, setCreatingGoal] = useState(false);
  const [selectedGoalForChart, setSelectedGoalForChart] = useState(null);
  const [editingGoal, setEditingGoal] = useState(null); // For edit modal
  
  // Parse time string to minutes
  const parseTimeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const match = timeStr.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (!match) return 0;
    return parseInt(match[1]) * 60 + parseInt(match[2]) + (match[3] ? parseInt(match[3]) / 60 : 0);
  };
  
  // Detect days in pasted text with improved algorithm
  const detectDaysInText = useCallback((text, customSplits = []) => {
    if (!text.trim()) {
      setDetectedDays(0);
      setParsedLines(0);
      setDetectedBlocks([]);
      return;
    }
    
    const lines = text.trim().split('\n').filter(l => l.trim());
    const parsedData = [];
    
    // Parse all lines - handle format where start time is on previous line
    let pendingStartTime = null;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const lower = line.toLowerCase();
      
      // Skip header lines
      if (lower.includes('inicio') || lower.includes('start') || lower.includes('fim') || 
          lower.includes('tempo exec') || lower.includes('perímetro') || lower.includes('cutting') ||
          lower.includes('marker name') || lower.includes('nome plano')) {
        continue;
      }
      
      const parts = line.split('\t');
      const firstPart = parts[0]?.trim();
      
      // Check if this line is just a time (start time for next operation)
      const isJustTime = firstPart && /^\d{1,2}:\d{2}(:\d{2})?$/.test(firstPart) && parts.length === 1;
      
      if (isJustTime) {
        // This is a start time line - save it for the next line
        pendingStartTime = firstPart;
        continue;
      }
      
      // Check if this line has the full data (with end time in first column or no time)
      if (parts.length >= 3) {
        const timeCell = parts[0].trim();
        const timeLines = timeCell.split('\n');
        
        let startTime, endTime;
        
        // Check if we have a pending start time from previous line
        if (pendingStartTime) {
          startTime = pendingStartTime;
          endTime = timeLines[0]?.trim() || '';
          pendingStartTime = null;
        } else {
          // Both times in same cell
          startTime = timeLines[0]?.trim();
          endTime = timeLines[1]?.trim() || '';
        }
        
        const timeMatch = startTime?.match(/^(\d{1,2}):(\d{2})/);
        if (timeMatch) {
          const minutes = parseTimeToMinutes(startTime);
          const planName = parts[parts.length - 1]?.trim() || '';
          parsedData.push({
            lineIndex: i,
            startTime,
            endTime,
            minutes,
            hour: parseInt(timeMatch[1]),
            planName: planName.substring(0, 50)
          });
        }
      }
    }
    
    if (parsedData.length === 0) {
      setDetectedDays(0);
      setParsedLines(0);
      setDetectedBlocks([]);
      return;
    }
    
    // Detect day boundaries
    const splits = [...customSplits]; // Start with manual splits
    let lastMinutes = parsedData[0].minutes;
    let lastHour = parsedData[0].hour;
    
    for (let i = 1; i < parsedData.length; i++) {
      const curr = parsedData[i];
      const lineIdx = curr.lineIndex;
      
      // Skip if already manually split
      if (splits.includes(lineIdx)) continue;
      
      const timeDiff = curr.minutes - lastMinutes;
      
      // Condition 1: Hour goes from afternoon (>12) to morning (<12)
      if (lastHour > 12 && curr.hour < 12) {
        splits.push(lineIdx);
      }
      // Condition 2: Time went backwards significantly (more than 2 hours / 120 min)
      // This catches cases where a new day starts in the afternoon
      else if (timeDiff < -120) {
        splits.push(lineIdx);
      }
      // Condition 3: Very large forward gap (>10 hours) - unusual, might be new day
      else if (timeDiff > 600) {
        splits.push(lineIdx);
      }
      
      lastMinutes = curr.minutes;
      lastHour = curr.hour;
    }
    
    // Sort splits
    splits.sort((a, b) => a - b);
    
    // Create blocks - splits contain line indices where new days start
    const blocks = [];
    let currentBlockStartIdx = 0; // Index in parsedData array
    
    for (let i = 0; i < parsedData.length; i++) {
      const curr = parsedData[i];
      
      // Check if this line starts a new day
      if (splits.includes(curr.lineIndex) && i > 0) {
        // Save the previous block
        const blockData = parsedData.slice(currentBlockStartIdx, i);
        if (blockData.length > 0) {
          blocks.push({
            startLineIndex: blockData[0].lineIndex,
            endLineIndex: blockData[blockData.length - 1].lineIndex,
            firstTime: blockData[0].startTime,
            lastTime: blockData[blockData.length - 1].endTime || blockData[blockData.length - 1].startTime,
            lineCount: blockData.length,
            firstPlan: blockData[0].planName,
            lastPlan: blockData[blockData.length - 1].planName
          });
        }
        currentBlockStartIdx = i;
      }
    }
    
    // Last block
    const lastBlockData = parsedData.slice(currentBlockStartIdx);
    if (lastBlockData.length > 0) {
      blocks.push({
        startLineIndex: lastBlockData[0].lineIndex,
        endLineIndex: lastBlockData[lastBlockData.length - 1].lineIndex,
        firstTime: lastBlockData[0].startTime,
        lastTime: lastBlockData[lastBlockData.length - 1].endTime || lastBlockData[lastBlockData.length - 1].startTime,
        lineCount: lastBlockData.length,
        firstPlan: lastBlockData[0].planName,
        lastPlan: lastBlockData[lastBlockData.length - 1].planName
      });
    }
    
    setDetectedBlocks(blocks);
    setDetectedDays(blocks.length);
    setParsedLines(parsedData.length);
  }, []);
  
  // Auto-set end date based on detected days if start date is set
  useEffect(() => {
    if (detectedBlocks.length > 0 && pasteStartDate && !pasteEndDate) {
      const start = new Date(pasteStartDate);
      const end = new Date(start);
      end.setDate(end.getDate() + detectedBlocks.length - 1);
      setPasteEndDate(end.toISOString().split('T')[0]);
    }
  }, [detectedBlocks.length, pasteStartDate, pasteEndDate]);
  
  // Toggle manual split at a line
  const toggleManualSplit = (lineIndex) => {
    setManualSplits(prev => {
      const newSplits = prev.includes(lineIndex)
        ? prev.filter(i => i !== lineIndex)
        : [...prev, lineIndex];
      // Re-detect with new splits
      detectDaysInText(pasteText, newSplits);
      return newSplits;
    });
  };
  
  // Merge block with previous (remove split)
  const mergeWithPrevious = (blockIndex) => {
    if (blockIndex <= 0 || blockIndex >= detectedBlocks.length) return;
    const block = detectedBlocks[blockIndex];
    // Remove the split at the start of this block
    setManualSplits(prev => {
      const newSplits = prev.filter(i => i !== block.startLineIndex);
      detectDaysInText(pasteText, newSplits);
      return newSplits;
    });
  };
  
  // Split block at a specific position (add manual split)
  const splitBlock = (blockIndex, position = 'middle') => {
    if (blockIndex < 0 || blockIndex >= detectedBlocks.length) return;
    const block = detectedBlocks[blockIndex];
    // Calculate middle line index
    const midLine = Math.floor((block.startLineIndex + block.endLineIndex) / 2);
    if (midLine > block.startLineIndex) {
      setManualSplits(prev => {
        const newSplits = [...prev, midLine];
        detectDaysInText(pasteText, newSplits);
        return newSplits;
      });
    }
  };
  
  // Update detection when text changes - use JSON.stringify to stabilize array dependency
  const manualSplitsKey = JSON.stringify(manualSplits);
  useEffect(() => {
    detectDaysInText(pasteText, manualSplits);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pasteText, detectDaysInText, manualSplitsKey]);
  
  // Calculate date range
  const getDateRange = useCallback(() => {
    if (!pasteStartDate || !pasteEndDate) return [];
    const start = new Date(pasteStartDate);
    const end = new Date(pasteEndDate);
    const dates = [];
    const current = new Date(start);
    while (current <= end) {
      dates.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  }, [pasteStartDate, pasteEndDate]);
  
  // Use useMemo to prevent creating new array reference on every render
  const dateRange = useMemo(() => getDateRange(), [getDateRange]);
  const dateRangeCount = dateRange.length;
  
  // Reset modal state when opening
  const openPasteModal = () => {
    setPasteStartDate('');
    setPasteEndDate('');
    setPasteText('');
    setDetectedDays(0);
    setParsedLines(0);
    setExistingDatesInRange([]);
    setDaysToRemove([]);
    setDetectedBlocks([]);
    setManualSplits([]);
    setShowPasteModal(true);
  };
  
  // Handle removing a day from selection
  const toggleDayRemoval = (date) => {
    setDaysToRemove(prev => 
      prev.includes(date) 
        ? prev.filter(d => d !== date)
        : [...prev, date]
    );
  };
  
  // Get effective date range (excluding removed days) - memoized to prevent re-renders
  const effectiveDateRange = useMemo(() => 
    dateRange.filter(d => !daysToRemove.includes(d)),
    [dateRange, daysToRemove]
  );
  
  // Simulator state
  const [simEntreCortes, setSimEntreCortes] = useState(2);
  
  // Excel upload state
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Imports history state
  const [importHistory, setImportHistory] = useState([]);
  
  // Check for existing dates when range changes (must be after importHistory declaration)
  useEffect(() => {
    if (dateRange.length > 0 && importHistory.length > 0) {
      const existing = dateRange.filter(d => 
        importHistory.some(h => h.date === d)
      );
      setExistingDatesInRange(existing);
    } else {
      setExistingDatesInRange([]);
    }
  }, [dateRange, importHistory]);

  const saveSettings = (newSettings) => {
    setSettings(newSettings);
    localStorage.setItem("cutting-settings", JSON.stringify(newSettings));
    toast.success('Configurações guardadas');
  };

  const fetchSummary = useCallback(async () => {
    try {
      let url = `${API_BASE}/summary`;
      const params = [];
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      if (adjusted) params.push(`adjusted=true`);
      if (params.length) url += `?${params.join("&")}`;
      const res = await api.get(url);
      setSummary(res.data);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, adjusted]);

  const fetchDayDetail = useCallback(async (date) => {
    try {
      const qs = adjusted ? '?adjusted=true' : '';
      const res = await api.get(`${API_BASE}/days/${date}${qs}`);
      setDayDetail(res.data);
    } catch (e) {
    }
  }, [adjusted]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  // Reset pasting state when anomalous review modal is shown
  useEffect(() => {
    if (showAnomalousReviewModal) {
      setPasting(false);
    }
  }, [showAnomalousReviewModal]);

  // Calculate filtered summary data based on dashboard period
  const filteredSummary = useMemo(() => {
    if (!summary || !summary.daily_summary) return summary;
    
    // If 'all', return original summary
    if (dashboardPeriod === 'all') return summary;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let filteredDays = summary.daily_summary;
    
    if (dashboardPeriod === 'today') {
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      filteredDays = summary.daily_summary.filter(d => d.date === todayStr);
    } else if (dashboardPeriod === 'week') {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      filteredDays = summary.daily_summary.filter(d => {
        const dayDate = new Date(d.date);
        return dayDate >= weekAgo && dayDate <= today;
      });
    } else if (dashboardPeriod === 'month') {
      const monthAgo = new Date(today);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      filteredDays = summary.daily_summary.filter(d => {
        const dayDate = new Date(d.date);
        return dayDate >= monthAgo && dayDate <= today;
      });
    } else if (dashboardPeriod === 'custom' && customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      end.setHours(23, 59, 59, 999); // Include entire end day
      filteredDays = summary.daily_summary.filter(d => {
        const dayDate = new Date(d.date);
        return dayDate >= start && dayDate <= end;
      });
    }
    
    if (filteredDays.length === 0) {
      return {
        ...summary,
        total_cuts: 0,
        total_perimeter: 0,
        total_cutting_time_hours: 0,
        total_cutting_time_min: 0,
        total_machine_stopped_hours: 0,
        total_machine_stopped_min: 0,
        total_between_markers_min: 0,
        total_real_time_hours: 0,
        efficiency: 0,
        avg_entre_cortes: 0,
        avg_speed: 0,
        total_days: 0,
        daily_summary: []
      };
    }
    
    // Recalculate aggregated metrics from filtered days
    const totalCuts = filteredDays.reduce((sum, d) => sum + (d.total_cuts || d.num_marcadas || 0), 0);
    const totalPerimeter = filteredDays.reduce((sum, d) => sum + (d.total_perimeter || 0), 0);
    const totalCuttingTime = filteredDays.reduce((sum, d) => sum + (d.total_cutting_time || 0), 0);
    const totalMachineStopped = filteredDays.reduce((sum, d) => sum + (d.total_machine_stopped || 0), 0);
    const totalBetweenMarkers = filteredDays.reduce((sum, d) => sum + (d.total_entre_cortes || 0), 0);
    const totalRealTime = totalCuttingTime + totalMachineStopped + totalBetweenMarkers;
    
    const efficiency = totalRealTime > 0 ? ((totalCuttingTime / totalRealTime) * 100) : 0;
    const avgEntreCortes = totalCuts > 0 ? (totalBetweenMarkers / totalCuts) : 0;
    const avgSpeed = totalCuttingTime > 0 ? (totalPerimeter / totalCuttingTime) : 0;
    
    const totalTimeOutsideHoursMin = filteredDays.reduce((sum, d) => sum + (d.time_outside_hours || 0), 0);
    
    return {
      ...summary,
      total_cuts: totalCuts,
      total_perimeter: totalPerimeter,
      total_cutting_time_hours: totalCuttingTime / 60,
      total_cutting_time_min: totalCuttingTime,
      total_machine_stopped_hours: totalMachineStopped / 60,
      total_machine_stopped_min: totalMachineStopped,
      total_between_markers_min: totalBetweenMarkers,
      total_entre_cortes_min: totalBetweenMarkers,
      total_real_time_hours: totalRealTime / 60,
      efficiency: parseFloat(efficiency.toFixed(2)),
      corte_pct: totalRealTime > 0 ? parseFloat((totalCuttingTime / totalRealTime * 100).toFixed(1)) : 0,
      restante_pct: totalRealTime > 0 ? parseFloat((totalMachineStopped / totalRealTime * 100).toFixed(1)) : 0,
      entre_cortes_pct: totalRealTime > 0 ? parseFloat((totalBetweenMarkers / totalRealTime * 100).toFixed(1)) : 0,
      avg_entre_cortes: parseFloat(avgEntreCortes.toFixed(2)),
      avg_speed: parseFloat(avgSpeed.toFixed(2)),
      total_days: filteredDays.length,
      total_time_outside_hours_min: totalTimeOutsideHoursMin,
      total_time_outside_hours_hours: totalTimeOutsideHoursMin / 60,
      daily_summary: filteredDays
    };
  }, [summary, dashboardPeriod, customStartDate, customEndDate]);

  useEffect(() => {
    if (selectedDay) fetchDayDetail(selectedDay);
    else setDayDetail(null);
  }, [selectedDay, fetchDayDetail]);

  const handleDataSuccess = () => { fetchSummary(); fetchImportHistory(); };

  // ========== Goals Management Functions ==========
  const fetchGoalsRef = useRef(false);
  
  const fetchGoals = useCallback(async () => {
    if (fetchGoalsRef.current) return; // Prevent duplicate fetches using ref
    fetchGoalsRef.current = true;
    setLoadingGoals(true);
    try {
      const res = await api.get(`${API_BASE}/goals`);
      setGoals(res.data || []);
    } catch (e) {
      setGoals([]);
    } finally {
      setLoadingGoals(false);
      fetchGoalsRef.current = false;
    }
  }, []); // Sem dependências que causem re-criação

  // Only fetch goals once on mount
  const goalsInitialized = useRef(false);
  useEffect(() => { 
    if (!goalsInitialized.current) {
      goalsInitialized.current = true;
      fetchGoals(); 
    }
  }, [fetchGoals]);

  const createGoal = async () => {
    if (!newGoal.name.trim()) {
      toast.error('Insira um nome para a meta');
      return;
    }
    if (!newGoal.periodStart || !newGoal.periodEnd) {
      toast.error('Selecione o período de tempo');
      return;
    }
    if (newGoal.periodStart > newGoal.periodEnd) {
      toast.error('A data inicial deve ser anterior à data final');
      return;
    }

    setCreatingGoal(true);
    try {
      const res = await api.post(`${API_BASE}/goals`, {
        name: newGoal.name,
        period_start: newGoal.periodStart,
        period_end: newGoal.periodEnd,
        parameters: {
          between_markers_reduction: newGoal.betweenMarkersReduction,
          machine_stopped_reduction: newGoal.machineStoppedReduction,
          target_between_markers_time: newGoal.targetBetweenMarkersTime,
          target_efficiency: newGoal.targetEfficiency,
          target_machine_stopped_percent: newGoal.targetMachineStoppedPercent
        },
        is_active: true
      });
      toast.success('Meta criada com sucesso!');
      setGoals(prev => [res.data, ...prev]);
      setShowCreateGoalModal(false);
      setNewGoal({
        name: '',
        periodStart: '',
        periodEnd: '',
        betweenMarkersReduction: 20,
        machineStoppedReduction: 15,
        targetBetweenMarkersTime: 2.0,
        targetEfficiency: 35,
        targetMachineStoppedPercent: 10
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar meta');
    } finally {
      setCreatingGoal(false);
    }
  };

  const toggleGoalActive = async (goalId) => {
    try {
      const res = await api.patch(`${API_BASE}/goals/${goalId}/toggle-active`);
      setGoals(prev => prev.map(g => g.id === goalId ? { ...g, is_active: res.data.is_active } : g));
      toast.success(res.data.is_active ? 'Meta activada' : 'Meta desactivada');
    } catch (e) {
      toast.error('Erro ao alterar estado da meta');
    }
  };

  const deleteGoal = async (goalId) => {
    if (!window.confirm('Tem certeza que deseja eliminar esta meta?')) return;
    try {
      await api.delete(`${API_BASE}/goals/${goalId}`);
      setGoals(prev => prev.filter(g => g.id !== goalId));
      toast.success('Meta eliminada');
      if (selectedGoalForChart === goalId) setSelectedGoalForChart(null);
    } catch (e) {
      toast.error('Erro ao eliminar meta');
    }
  };

  const refreshGoal = async (goalId) => {
    try {
      const res = await api.post(`${API_BASE}/goals/${goalId}/refresh`);
      setGoals(prev => prev.map(g => g.id === goalId ? res.data : g));
      toast.success('Dados da meta atualizados');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao atualizar meta');
    }
  };

  const openEditGoal = (goal) => {
    setEditingGoal({
      ...goal,
      name: goal.name,
      betweenMarkersReduction: goal.parameters?.between_markers_reduction || 20,
      machineStoppedReduction: goal.parameters?.machine_stopped_reduction || 15,
      targetBetweenMarkersTime: goal.parameters?.target_between_markers_time || 2.0,
      targetEfficiency: goal.parameters?.target_efficiency || 35,
      targetMachineStoppedPercent: goal.parameters?.target_machine_stopped_percent || 10
    });
  };

  const saveEditGoal = async () => {
    if (!editingGoal) return;
    try {
      const res = await api.put(`${API_BASE}/goals/${editingGoal.id}`, {
        name: editingGoal.name,
        parameters: {
          between_markers_reduction: editingGoal.betweenMarkersReduction,
          machine_stopped_reduction: editingGoal.machineStoppedReduction,
          target_between_markers_time: editingGoal.targetBetweenMarkersTime,
          target_efficiency: editingGoal.targetEfficiency,
          target_machine_stopped_percent: editingGoal.targetMachineStoppedPercent
        }
      });
      setGoals(prev => prev.map(g => g.id === editingGoal.id ? res.data : g));
      setEditingGoal(null);
      toast.success('Meta atualizada com sucesso!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao atualizar meta');
    }
  };

  const generateGoalPDF = async (goal) => {
    try {
      toast.loading('A gerar PDF...', { id: 'pdf' });
      const res = await api.get(`${API_BASE}/goals/${goal.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `meta_${goal.name.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF gerado com sucesso!', { id: 'pdf' });
    } catch (e) {
      toast.error('Erro ao gerar PDF', { id: 'pdf' });
    }
  };
  // ========== End Goals Management ==========

  // Validate before importing
  const validateAndImport = () => {
    if (!pasteText.trim()) {
      toast.error('Cole os dados da tabela');
      return;
    }
    if (!pasteStartDate) {
      toast.error('Selecione a data inicial');
      return;
    }
    
    // Check if detected days match effective range
    if (detectedDays !== effectiveDateRange.length) {
      setShowDayMismatchModal(true);
      return;
    }
    
    // Check for existing data
    const effectiveExisting = existingDatesInRange.filter(d => !daysToRemove.includes(d));
    if (effectiveExisting.length > 0) {
      setShowReplaceConfirm(true);
      return;
    }
    
    // All good, proceed
    handlePasteData(false);
  };

  const handlePasteData = async (replace = false) => {
    if (effectiveDateRange.length === 0) {
      toast.error('Nenhum dia seleccionado');
      return;
    }
    
    setPasting(true);
    setShowReplaceConfirm(false);
    
    try {
      // First, get preview with break detection
      const previewRes = await api.post(`${API_BASE}/paste/preview`, {
        dates: effectiveDateRange,
        text: pasteText,
        replace: replace
      });
      
      const preview = previewRes.data;
      
      // If breaks were detected, show review modal
      if (preview.has_breaks && preview.detected_breaks.length > 0) {
        setPreviewData(preview);
        setPreviewReplace(replace);
        // Initialize break deductions with detected values
        setBreakDeductions(preview.detected_breaks.map(brk => {
          // Auto-selecionar a fonte correta: se Entre Marcadas tem mais tempo que Parada
          // e é suficiente para a dedução, usar Entre Marcadas
          const stoppedVal = brk.record_machine_stopped || 0;
          const betweenVal = brk.record_between_markers || 0;
          const deductMin = brk.minutes_to_deduct || 0;
          
          // Decidir automaticamente de onde descontar:
          // 1. Se Entre Marcadas é suficiente e maior que Parada -> usar Entre Marcadas
          // 2. Senão, se Parada é suficiente -> usar Parada
          // 3. Caso contrário, manter o valor do backend
          let autoDeductFrom = brk.deduct_from;
          if (betweenVal >= deductMin && betweenVal > stoppedVal) {
            autoDeductFrom = 'between_markers';
          } else if (stoppedVal >= deductMin) {
            autoDeductFrom = 'machine_stopped';
          }
          
          return {
            ...brk,
            deduct_from: autoDeductFrom,
            enabled: true
          };
        }));
        setShowPasteModal(false);
        setShowBreakReviewModal(true);
      } else {
        // No breaks detected, proceed with direct save
        await confirmPasteData(replace, []);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao processar dados');
    } finally {
      setPasting(false);
    }
  };
  
  // Confirm paste with break deductions
  const confirmPasteData = async (replace, deductions) => {
    setPasting(true);
    try {
      // Filter only enabled deductions and format for API
      const enabledDeductions = deductions
        .filter(d => d.enabled !== false)
        .map(d => ({
          record_index: d.record_index,
          break_type: d.break_type,
          break_name: d.break_name,
          minutes_to_deduct: d.minutes_to_deduct,
          deduct_from: d.deduct_from,
          start_time: d.start_time,
          end_time: d.end_time
        }));
      
      const res = await api.post(`${API_BASE}/paste/confirm`, {
        dates: effectiveDateRange,
        text: pasteText,
        replace: replace,
        break_deductions: enabledDeductions
      });
      
      // Store the dates that were imported for anomalous check
      const importedDates = res.data.dates_processed || effectiveDateRange;
      
      toast.success(res.data.message);
      setShowBreakReviewModal(false);
      setPreviewData(null);
      setBreakDeductions([]);
      
      // Check for anomalous records in the imported dates
      await checkForAnomalousRecords(importedDates);
      
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao guardar dados');
      setPasting(false);
    }
  };
  
  // Check for anomalous records after import (Step 2)
  const checkForAnomalousRecords = async (dates) => {
    try {
      const res = await api.get(`${API_BASE}/anomalous-summary`);
      const summary = res.data;
      
      // Filter only anomalous records from the imported dates
      const relevantAnomalous = [];
      if (summary.days_with_anomalous) {
        for (const day of summary.days_with_anomalous) {
          if (dates.includes(day.date) && day.unclassified_count > 0) {
            relevantAnomalous.push(...day.anomalous_records.filter(r => !r.classification));
          }
        }
      }
      
      if (relevantAnomalous.length > 0) {
        // Show anomalous records modal for classification
        setAnomalousRecords(relevantAnomalous);
        setAnomalousClassifications({});
        setShowAnomalousReviewModal(true);
        // Reset pasting state so buttons work in modal
        setPasting(false);
      } else {
        // No anomalous records, complete import
        completeImport();
      }
    } catch (e) {
      // Continue with import even if check fails
      completeImport();
    }
  };
  
  // Complete the import process
  const completeImport = () => {
    setPasteText('');
    setDetectedDays(0);
    setParsedLines(0);
    setShowPasteModal(false);
    setShowAnomalousReviewModal(false);
    setAnomalousRecords([]);
    setAnomalousClassifications({});
    handleDataSuccess();
    setPasting(false);
  };
  
  // Handle anomalous record classification change
  const handleAnomalousClassification = (record, reasonId, minutesToExtract = null) => {
    const key = `${record.date}_${record.record_index}_${record.field_type}`;
    const extractMinutes = minutesToExtract !== null ? minutesToExtract : record.original_value;
    
    setAnomalousClassifications(prev => ({
      ...prev,
      [key]: {
        record_index: record.record_index,
        date: record.date,
        classification: reasonId ? 'exclude' : null, // Always exclude if reason is selected
        field_type: record.field_type,
        original_value: record.original_value,
        minutes_to_extract: extractMinutes,
        reason: reasonId,
        reason_name: settings.anomalousReasons?.find(r => r.id === reasonId)?.name || reasonId
      }
    }));
  };
  
  // Update minutes to extract for a record
  const updateMinutesToExtract = (record, minutes) => {
    const key = `${record.date}_${record.record_index}_${record.field_type}`;
    if (!anomalousClassifications[key]) return;
    
    setAnomalousClassifications(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        minutes_to_extract: Math.min(Math.max(0, minutes), record.original_value)
      }
    }));
  };
  
  // Get anomalous reasons from settings
  const getAnomalousReasons = () => {
    return settings.anomalousReasons || [
      { id: 'breakdown', name: 'Avaria', color: '#ef4444', icon: '🔧' },
      { id: 'material', name: 'Falta de Material', color: '#f59e0b', icon: '📦' },
      { id: 'setup', name: 'Setup/Preparação', color: '#3b82f6', icon: '⚙️' },
      { id: 'maintenance', name: 'Manutenção Preventiva', color: '#8b5cf6', icon: '🛠️' },
      { id: 'operator', name: 'Ausência Operador', color: '#ec4899', icon: '👤' },
      { id: 'other', name: 'Outro', color: '#6b7280', icon: '📝' },
    ];
  };
  
  // Confirm anomalous classifications and complete import
  const confirmAnomalousClassifications = async () => {
    const toSave = Object.values(anomalousClassifications);
    
    // Set pasting to true to show loading state
    setPasting(true);
    
    try {
      if (toSave.length > 0) {
        // Transform to match backend expected format
        const formattedClassifications = toSave.map(c => ({
          record_index: c.record_index,
          date: c.date,
          classification: c.classification || 'exclude',
          field_type: c.field_type,
          original_value: c.original_value,
          reason: c.reason_name || c.reason || 'Classificado'
        }));
        
        const response = await api.post(`${API_BASE}/anomalous/classify`, { 
          classifications: formattedClassifications 
        });
        
        // Check for not_found results
        const notFound = response.data?.results?.filter(r => r.status === 'not_found');
        if (notFound?.length > 0) {
          toast.warning(`Alguns dias não foram encontrados na base de dados`);
        } else {
          toast.success(`${toSave.length} registo(s) anormal(is) classificado(s)`);
        }
      }
      
      completeImport();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao classificar registos anormais');
      setPasting(false);
    }
  };
  
  // Skip anomalous classification and complete import
  const skipAnomalousClassification = () => {
    toast.info('Registos anormais podem ser classificados mais tarde na aba "Registos Anormais"');
    completeImport();
  };
  
  // Toggle break deduction enabled/disabled
  const toggleBreakDeduction = (index) => {
    setBreakDeductions(prev => prev.map((d, i) => 
      i === index ? { ...d, enabled: !d.enabled } : d
    ));
  };
  
  // Change which column to deduct from
  const changeDeductFrom = (index, newTarget) => {
    setBreakDeductions(prev => prev.map((d, i) => 
      i === index ? { ...d, deduct_from: newTarget } : d
    ));
  };

  const handleDeleteDay = async (date) => {
    try {
      await api.delete(`${API_BASE}/days/${date}`);
      toast.success(`Dados de ${date} eliminados`);
      // Update both summary and import history to keep state in sync
      fetchSummary();
      fetchImportHistory();
      if (selectedDay === date) setSelectedDay(null);
    } catch (e) {
      toast.error('Erro ao eliminar');
    }
  };

  const handleDeleteAll = async () => {
    setDeleting(true);
    try {
      await api.delete(`${API_BASE}/days`);
      setSummary(null);
      setSelectedDay(null);
      setDayDetail(null);
      setImportHistory([]);  // Clear import history immediately
      fetchSummary();
      fetchImportHistory();
      setStartDate('');
      setEndDate('');
      toast.success('Todos os dados eliminados');
    } catch (e) {
      toast.error('Erro ao eliminar');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleDateClear = () => { setStartDate(''); setEndDate(''); };

  const hasData = summary?.has_data;
  
  // Export CSV handler
  const handleExportCSV = () => {
    if (!summary?.daily_summary) return;
    
    const headers = ['Data', 'Cortes', 'Perímetro (m)', 'Tempo Corte (min)', 'Eficiência (%)', 'Velocidade (m/min)', 'Entrada', 'Saída'];
    const rows = summary.daily_summary.map(day => [
      day.date,
      day.total_cuts,
      day.total_perimeter.toFixed(1),
      day.total_cutting_time.toFixed(1),
      day.efficiency,
      day.avg_real_speed,
      day.first_start,
      day.last_end
    ]);
    
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\n');
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `corte_relatorio_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exportado com sucesso');
  };
  
  // Export PDF handler (simple print version)
  const handleExportPDF = () => {
    if (!summary) return;
    
    // Helper to format minutes to HH:MM:SS
    const fmtMin = (m) => {
      if (!m || m <= 0) return '-';
      const h = Math.floor(m / 60);
      const min = Math.floor(m % 60);
      const s = Math.round((m % 1) * 60);
      return h > 0 ? `${h}:${String(min).padStart(2,'0')}:${String(s).padStart(2,'0')}` : `${min}:${String(s).padStart(2,'0')}`;
    };

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Relatório de Corte - ${new Date().toLocaleDateString('pt-PT')}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; font-size: 10px; }
          h1 { color: #0ea5e9; margin-bottom: 5px; font-size: 20px; }
          h2 { color: #374151; font-size: 13px; margin-top: 20px; }
          .subtitle { color: #6b7280; font-size: 11px; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 9px; }
          th, td { border: 1px solid #e5e7eb; padding: 4px 5px; text-align: center; }
          th { background: #1e293b; color: white; font-weight: 600; font-size: 8px; text-transform: uppercase; }
          .metric { display: inline-block; margin-right: 25px; margin-bottom: 10px; }
          .metric-value { font-size: 16px; font-weight: bold; color: #0ea5e9; }
          .metric-label { font-size: 9px; color: #6b7280; text-transform: uppercase; }
          .good { color: #10b981; font-weight: bold; }
          .warn { color: #f59e0b; font-weight: bold; }
          .bad { color: #ef4444; font-weight: bold; }
          .sky { color: #0ea5e9; }
          .amber { color: #f59e0b; }
          .cyan { color: #06b6d4; }
          .mono { font-family: monospace; }
          td:first-child { text-align: left; }
          @media print { body { padding: 0; } @page { size: landscape; margin: 10mm; } }
        </style>
      </head>
      <body>
        <h1>Painel de Corte</h1>
        <div class="subtitle">Relatório gerado em ${new Date().toLocaleString('pt-PT')}</div>
        
        <h2>RESUMO GERAL</h2>
        <div>
          <div class="metric">
            <div class="metric-value">${summary.efficiency}%</div>
            <div class="metric-label">Eficiência</div>
          </div>
          <div class="metric">
            <div class="metric-value">${summary.total_cuts}</div>
            <div class="metric-label">Total Cortes</div>
          </div>
          <div class="metric">
            <div class="metric-value">${summary.total_perimeter?.toFixed(0)}m</div>
            <div class="metric-label">Perímetro Total</div>
          </div>
          <div class="metric">
            <div class="metric-value">${fmtMin(summary.total_cutting_time_min)}</div>
            <div class="metric-label">Tempo de Corte</div>
          </div>
          <div class="metric">
            <div class="metric-value">${summary.avg_entre_cortes} min</div>
            <div class="metric-label">Média Entre Marcadas</div>
          </div>
        </div>
        
        <h2>DADOS DIÁRIOS</h2>
        <table>
          <thead>
            <tr>
              <th>Data</th>
              <th>Cortes</th>
              <th>% Corte</th>
              <th>% Intra Marc.</th>
              <th>% Entre Marc.</th>
              <th>T.Corte</th>
              <th>T.Intra Marc.</th>
              <th>T.Entre Marc.</th>
              <th>T.Total</th>
              <th>Méd.E.Marc</th>
              <th>Deduções</th>
              <th>Vel.Média</th>
              <th>Vel.Real</th>
              <th>Perímetro</th>
              <th>Fora Hor.</th>
              <th>Horário</th>
            </tr>
          </thead>
          <tbody>
            ${summary.daily_summary?.map(day => {
              const elapsed = (day.last_end_minutes || 0) - (day.first_start_minutes || 0);
              const overtimeGap = day.overtime_gap_outside_hours || 0;
              const tTotal = Math.max(0, elapsed - 110 - overtimeGap);
              const avgEntre = day.total_cuts > 0 ? (day.total_entre_cortes || 0) / day.total_cuts : 0;
              return `
              <tr>
                <td>${day.date}</td>
                <td>${day.total_cuts}</td>
                <td class="${day.efficiency >= 35 ? 'good' : 'bad'}">${day.efficiency}%</td>
                <td class="amber">${(day.pct_parada || 0).toFixed(1)}%</td>
                <td class="cyan">${(day.pct_entre || 0).toFixed(1)}%</td>
                <td class="mono sky">${fmtMin(day.total_cutting_time)}</td>
                <td class="mono amber">${fmtMin(day.total_machine_stopped)}</td>
                <td class="mono cyan">${fmtMin(day.total_entre_cortes)}</td>
                <td class="mono sky">${fmtMin(tTotal)}</td>
                <td class="mono">${fmtMin(avgEntre)}</td>
                <td class="mono" style="color:#8b5cf6">${day.total_break_deductions > 0 ? '-' + fmtMin(day.total_break_deductions) : '-'}</td>
                <td>${(day.avg_real_speed || 0).toFixed(1)}</td>
                <td class="good">${(day.weighted_avg_speed || 0).toFixed(1)}</td>
                <td>${day.total_perimeter ? Math.round(day.total_perimeter).toLocaleString('pt-PT') + ' m' : '-'}</td>
                <td class="amber">${day.time_outside_hours > 0 ? fmtMin(day.time_outside_hours) : '-'}</td>
                <td class="mono">${day.first_start || '-'} - ${day.last_end || '-'}</td>
              </tr>`;
            }).join('') || ''}
          </tbody>
        </table>
      </body>
      </html>
    `;
    
    if (!safePrintHTML(htmlContent)) {
      toast.error('Popup bloqueado. Permita popups para exportar PDF.');
    }
    toast.success('PDF pronto para impressão');
  };
  
  // Fetch import history
  const fetchImportHistory = useCallback(async () => {
    try {
      const res = await api.get(`${API_BASE}/days`);
      setImportHistory(res.data || []);
    } catch (e) {
    }
  }, []);
  
  useEffect(() => {
    if (hasData) {
      fetchImportHistory();
    }
  }, [hasData, fetchImportHistory]);
  
  // Excel upload handler
  const handleExcelUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      toast.error('Ficheiro deve ser Excel (.xlsx ou .xls)');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post(`${API_BASE}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(res.data.message);
      handleDataSuccess();
      fetchImportHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao importar ficheiro Excel');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Calculate simulator metrics
  const simulatedMetrics = summary ? (() => {
    const currentEntreCortes = summary.avg_entre_cortes || 0;
    const targetEntreCortes = simEntreCortes;
    const timeSaved = (currentEntreCortes - targetEntreCortes) * (summary.total_cuts || 0);
    const newTotalTime = (summary.total_real_time_min || 0) - timeSaved;
    const newEfficiency = newTotalTime > 0 
      ? ((summary.total_cutting_time_min || 0) / newTotalTime * 100) 
      : 0;
    return {
      timeSaved: Math.max(0, timeSaved),
      timeSavedHours: Math.max(0, timeSaved / 60),
      newEfficiency: Math.min(100, Math.max(0, newEfficiency)),
      efficiencyGain: Math.max(0, newEfficiency - (summary.efficiency || 0))
    };
  })() : null;

  return (
    <div className={`min-h-screen ${t.bg} relative transition-colors duration-300`} data-testid="cutting-simulator-page">
      {/* Texture overlay for dark mode */}
      {t.texture && (
        <div className="fixed inset-0 opacity-[0.03] pointer-events-none"
          style={{ 
            backgroundImage: `url(https://images.unsplash.com/photo-1720036236697-018370867320?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxkYXJrJTIwaW5kdXN0cmlhbCUyMGZhY3RvcnklMjBhYnN0cmFjdCUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc0NTM5NjUxfDA&ixlib=rb-4.1.0&q=85`,
            backgroundSize: "cover", 
            backgroundPosition: "center" 
          }} 
        />
      )}

      <div className="relative z-10 w-full max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8">
        {/* Header */}
        <header className="flex flex-col gap-3 mb-6" data-testid="dashboard-header">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-shrink">
              <div className="w-10 h-10 rounded-lg bg-sky-500/10 border border-sky-500/20 flex items-center justify-center shrink-0">
                <Scissors className="w-5 h-5 text-sky-500" />
              </div>
              <div className="min-w-0">
                <h1 className={`text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight ${t.text} truncate`} style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                  Painel de Corte
                </h1>
                <p className={`text-[10px] sm:text-xs ${t.textMut} tracking-wide uppercase truncate`}>Desempenho da Maquina de Corte</p>
              </div>
              {/* Última tarefa registada no sistema (último corte do dia mais recente) */}
              {(() => {
                const days = summary?.daily_summary || [];
                if (!days.length) return null;
                // Pick the most recent day by date
                const latest = days.reduce((acc, d) => (!acc || d.date > acc.date ? d : acc), null);
                if (!latest || !latest.last_end) return null;
                const [yy, mm, dd] = String(latest.date).split("-");
                const [hh, mi, ss] = String(latest.last_end).split(":");
                const ref = new Date(parseInt(yy), parseInt(mm) - 1, parseInt(dd), parseInt(hh) || 0, parseInt(mi) || 0, parseInt(ss) || 0);
                const diffMs = Date.now() - ref.getTime();
                let ago = "";
                if (diffMs >= 0) {
                  const mins = Math.floor(diffMs / 60000);
                  if (mins < 1) ago = "agora mesmo";
                  else if (mins < 60) ago = `há ${mins} min`;
                  else if (mins < 1440) ago = `há ${Math.floor(mins / 60)}h${String(mins % 60).padStart(2, "0")}`;
                  else ago = `há ${Math.floor(mins / 1440)} dias`;
                }
                return (
                  <div
                    data-testid="cs-last-task-chip"
                    className={`hidden md:flex items-center gap-3 ml-2 rounded-xl border ${t.card} px-4 py-2.5`}
                    title={`Último corte registado no sistema (${latest.date} ${latest.last_end})`}
                  >
                    <div className="h-9 w-9 rounded-lg bg-emerald-500/15 border border-emerald-500/30 grid place-items-center">
                      <Clock className="h-4 w-4 text-emerald-500" />
                    </div>
                    <div className="leading-tight">
                      <div className={`text-[10px] uppercase tracking-[0.18em] ${t.textMut}`}>Última tarefa</div>
                      <div className="flex items-baseline gap-2">
                        <span className={`font-mono text-base lg:text-lg tabular-nums ${t.text} font-bold`} data-testid="cs-last-task-time">
                          {dd}/{mm} {String(hh).padStart(2,"0")}:{String(mi).padStart(2,"0")}
                        </span>
                        {ago && (
                          <span className={`text-[11px] ${t.textMut}`} data-testid="cs-last-task-ago">{ago}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="flex flex-wrap items-center gap-2 justify-end">
              {/* Real vs Ajustado toggle */}
              <button
                data-testid="cutting-adjusted-toggle"
                onClick={() => setAdjusted((v) => !v)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 transition-colors text-sm font-medium ${
                  adjusted
                    ? 'bg-amber-500/15 border-amber-400/50 text-amber-600 hover:bg-amber-500/25'
                    : `${t.card} ${t.hoverCard}`
                }`}
                title={adjusted
                  ? 'Mostrando dados AJUSTADOS (com anomalias + justificações aplicadas). Clica para ver Reais.'
                  : 'Mostrando dados REAIS. Clica para ver Ajustados (com anomalias + justificações).'}
              >
                <AlertTriangle className="w-4 h-4" />
                {adjusted ? 'Ajustado' : 'Real'}
                {adjusted && summary?.adjustment_meta && (summary.adjustment_meta.justifications_applied + summary.adjustment_meta.excluded_records_count) > 0 && (
                  <span className="ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] bg-amber-300/40 tabular-nums">
                    {summary.adjustment_meta.justifications_applied + summary.adjustment_meta.excluded_records_count}
                  </span>
                )}
              </button>
              <button 
                data-testid="theme-toggle-btn" 
                onClick={toggle}
                className={`p-2 rounded-lg ${t.card} ${t.hoverCard} transition-all`}
                title={mode === "dark" ? "Modo claro" : "Modo escuro"}
              >
                {mode === "dark" ? (
                  <Sun className="w-4 h-4 text-amber-400" />
                ) : (
                  <Moon className="w-4 h-4 text-sky-500" />
                )}
              </button>
              {hasData && (
                <button 
                  data-testid="delete-all-btn" 
                  onClick={() => setShowDeleteConfirm(true)}
                  className={`p-2 rounded-lg ${t.card} hover:border-red-400/40 transition-all`} 
                  title="Apagar todos os dados"
                >
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              )}
              {/* Excel Upload Button */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleExcelUpload}
                className="hidden"
                id="excel-upload"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg ${t.card} border-2 border-emerald-500/30 text-emerald-600 hover:bg-emerald-500/10 transition-colors disabled:opacity-50`}
                title="Importar ficheiro Excel"
              >
                <FileSpreadsheet className="w-4 h-4" />
                <span className="text-sm font-medium">{uploading ? 'A importar...' : 'Excel'}</span>
              </button>
              {/* Paste Data Button */}
              <button
                onClick={() => openPasteModal()}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-500 text-white hover:bg-sky-600 transition-colors"
              >
                <ClipboardPaste className="w-4 h-4" />
                <span className="text-sm font-medium">Colar Dados</span>
              </button>
            </div>
          </div>

          {/* Filter bar */}
          {hasData && (
            <div className="flex items-center gap-3 flex-wrap" data-testid="filter-bar">
              <button
                onClick={() => setShowDateFilters(!showDateFilters)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm ${t.card} ${t.textSec} hover:opacity-80 transition-opacity`}
              >
                <Filter className="w-4 h-4" />
                Filtrar datas
              </button>
              {/* CSV Export */}
              <button
                onClick={() => handleExportCSV()}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm ${t.card} ${t.textSec} hover:opacity-80 transition-opacity`}
                title="Exportar para CSV"
              >
                <FileText className="w-4 h-4 text-emerald-500" />
                CSV
              </button>
              {/* PDF Export */}
              <button
                onClick={() => handleExportPDF()}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm ${t.card} ${t.textSec} hover:opacity-80 transition-opacity`}
                title="Exportar para PDF"
              >
                <FileText className="w-4 h-4 text-red-500" />
                PDF
              </button>
              {showDateFilters && (
                <>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className={`px-3 py-1.5 rounded-md text-sm ${t.inputBg} ${t.card} ${t.text}`}
                    />
                    <span className={`text-xs ${t.textMut}`}>até</span>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className={`px-3 py-1.5 rounded-md text-sm ${t.inputBg} ${t.card} ${t.text}`}
                    />
                  </div>
                  {(startDate || endDate) && (
                    <button
                      onClick={handleDateClear}
                      className={`px-3 py-1.5 rounded-md text-xs ${t.inner} ${t.textSec} hover:opacity-80 transition-opacity`}
                    >
                      Limpar
                    </button>
                  )}
                </>
              )}
              <span className={`text-xs ${t.textMut} ml-auto`}>
                {summary?.total_days} dia(s) | {summary?.total_cuts} cortes
              </span>
            </div>
          )}
        </header>

        {/* Delete All Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="delete-all-modal">
            <div className={`${t.card} rounded-xl p-6 max-w-md w-full mx-4`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className={`text-base font-semibold ${t.text}`} style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                    Apagar todos os dados?
                  </h3>
                  <p className={`text-xs ${t.textMut}`}>
                    {summary?.total_days} dia(s) e {summary?.total_cuts} cortes serão eliminados
                  </p>
                </div>
              </div>
              <p className={`text-sm ${t.textBody} mb-5`}>
                Esta ação não pode ser revertida. Todos os dados importados serão permanentemente eliminados.
              </p>
              <div className="flex justify-end gap-2">
                <button 
                  onClick={() => setShowDeleteConfirm(false)}
                  className={`px-4 py-2 text-sm rounded-md ${t.inner} ${t.textSec} hover:opacity-80 transition-opacity`}
                >
                  Cancelar
                </button>
                <button 
                  onClick={handleDeleteAll} 
                  disabled={deleting}
                  className="px-4 py-2 text-sm rounded-md bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50"
                >
                  {deleting ? "A eliminar..." : "Sim, apagar tudo"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Paste Data Modal */}
        {showPasteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className={`${t.card} rounded-xl overflow-hidden max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto`}>
              {/* Header */}
              <div className="bg-gradient-to-r from-sky-500 to-sky-600 p-4 text-white sticky top-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <ClipboardPaste className="w-6 h-6" />
                    <div>
                      <h3 className="text-lg font-semibold">Colar Dados da Tabela</h3>
                      <p className="text-xs text-sky-100">Importe dados copiados do software da máquina de corte</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => { setShowPasteModal(false); setPasteText(''); setDetectedDays(0); setParsedLines(0); setDaysToRemove([]); }}
                    className="p-1 hover:bg-white/20 rounded transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-5">
                {/* Step 1: Paste data first */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className={`text-sm font-medium ${t.text} uppercase tracking-wide flex items-center gap-2`}>
                      <span className="w-5 h-5 rounded-full bg-sky-500 text-white text-xs flex items-center justify-center">1</span>
                      Dados da Tabela
                    </label>
                    <button
                      onClick={async () => {
                        try {
                          const text = await navigator.clipboard.readText();
                          setPasteText(text);
                          // Reset dates and splits when pasting new data
                          setPasteStartDate('');
                          setPasteEndDate('');
                          setDaysToRemove([]);
                          setManualSplits([]);
                          setDetectedBlocks([]);
                          toast.success('Dados colados');
                        } catch (e) {
                          toast.error('Não foi possível aceder à área de transferência');
                        }
                      }}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs ${t.card} ${t.textSec} hover:opacity-80 transition-opacity`}
                    >
                      <ClipboardPaste className="w-3 h-3" />
                      Colar
                    </button>
                  </div>
                  <textarea
                    value={pasteText}
                    onChange={(e) => {
                      setPasteText(e.target.value);
                      // Reset when text changes significantly
                      if (!e.target.value.trim()) {
                        setPasteStartDate('');
                        setPasteEndDate('');
                        setDaysToRemove([]);
                        setManualSplits([]);
                        setDetectedBlocks([]);
                      }
                    }}
                    placeholder="Cole aqui os dados copiados da tabela (Ctrl+V)..."
                    className={`w-full h-44 p-4 rounded-lg text-sm font-mono ${t.inputBg} ${t.card} ${t.text} resize-none focus:ring-2 focus:ring-sky-500/50 focus:outline-none`}
                  />
                  <div className={`mt-2 text-xs ${t.textMut}`}>
                    {parsedLines} linha{parsedLines !== 1 ? 's' : ''} detectada{parsedLines !== 1 ? 's' : ''}
                    {detectedDays > 0 && (
                      <span className="ml-2">
                        • <span className="text-sky-500 font-medium">{detectedDays} dia{detectedDays > 1 ? 's' : ''}</span> de operações identificado{detectedDays > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>

                {/* Block Preview - show detected day blocks */}
                {detectedBlocks.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className={`text-sm font-medium ${t.text} uppercase tracking-wide flex items-center gap-2`}>
                        <span className="w-5 h-5 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">!</span>
                        Pré-visualização dos Dias Detectados
                      </label>
                      <span className={`text-xs ${t.textMut}`}>Clique para dividir/juntar dias</span>
                    </div>
                    <div className={`p-3 rounded-lg ${t.inner} space-y-2 max-h-48 overflow-y-auto`}>
                      {detectedBlocks.map((block, idx) => (
                        <div 
                          key={idx} 
                          className={`flex items-center gap-3 p-2 rounded-lg ${t.card} hover:border-sky-400/40 transition-all group`}
                        >
                          <div className="w-8 h-8 rounded-lg bg-sky-500/10 border border-sky-500/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-semibold text-sky-500">{idx + 1}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`text-sm font-medium ${t.text}`}>
                                {block.firstTime} → {block.lastTime}
                              </span>
                              <span className={`text-xs px-1.5 py-0.5 rounded ${t.inner} ${t.textMut}`}>
                                {block.lineCount} operações
                              </span>
                            </div>
                            <div className={`text-xs ${t.textMut} truncate`}>
                              {block.firstPlan}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {idx > 0 && (
                              <button
                                onClick={() => mergeWithPrevious(idx)}
                                className={`p-1.5 rounded text-xs ${t.card} text-amber-500 hover:bg-amber-500/10`}
                                title="Juntar com dia anterior"
                              >
                                ← Juntar
                              </button>
                            )}
                            {block.lineCount > 1 && (
                              <button
                                onClick={() => splitBlock(idx)}
                                className={`p-1.5 rounded text-xs ${t.card} text-sky-500 hover:bg-sky-500/10`}
                                title="Dividir este bloco em dois dias"
                              >
                                Dividir →
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className={`mt-2 text-xs ${t.textMut} text-center`}>
                      Use os botões para ajustar se a divisão automática não estiver correcta
                    </p>
                  </div>
                )}

                {/* Step 2: Date Range Selection - only show after data is pasted */}
                {detectedDays > 0 && (
                  <div>
                    <label className={`text-sm font-medium ${t.text} mb-2 block uppercase tracking-wide flex items-center gap-2`}>
                      <span className="w-5 h-5 rounded-full bg-sky-500 text-white text-xs flex items-center justify-center">2</span>
                      Seleccionar Datas ({detectedDays} dia{detectedDays > 1 ? 's' : ''} necessário{detectedDays > 1 ? 's' : ''})
                    </label>
                    <div className={`p-4 rounded-lg ${t.inner}`}>
                      <div className="flex items-center gap-4 flex-wrap">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-sky-500" />
                          <span className={`text-sm ${t.textMut}`}>Início:</span>
                          <input
                            type="date"
                            value={pasteStartDate}
                            onChange={(e) => {
                              setPasteStartDate(e.target.value);
                              // Auto-set end date based on detected days
                              if (e.target.value && detectedDays > 0) {
                                const start = new Date(e.target.value);
                                const end = new Date(start);
                                end.setDate(end.getDate() + detectedDays - 1);
                                setPasteEndDate(end.toISOString().split('T')[0]);
                                setDaysToRemove([]);
                              }
                            }}
                            className={`px-3 py-2 rounded-md text-sm ${t.inputBg} ${t.card} ${t.text}`}
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm ${t.textMut}`}>Fim:</span>
                          <input
                            type="date"
                            value={pasteEndDate}
                            onChange={(e) => {
                              setPasteEndDate(e.target.value);
                              setDaysToRemove([]);
                            }}
                            className={`px-3 py-2 rounded-md text-sm ${t.inputBg} ${t.card} ${t.text}`}
                          />
                        </div>
                        {effectiveDateRange.length > 0 && (
                          <span className={`text-sm font-medium ml-auto ${
                            effectiveDateRange.length === detectedDays ? 'text-emerald-500' : 'text-amber-500'
                          }`}>
                            {effectiveDateRange.length} dia{effectiveDateRange.length > 1 ? 's' : ''} seleccionado{effectiveDateRange.length > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      
                      {/* Day selection with remove option */}
                      {dateRange.length > 0 && (
                        <div className="mt-3">
                          <div className="flex flex-wrap gap-2">
                            {dateRange.map((d) => {
                              const isRemoved = daysToRemove.includes(d);
                              const isExisting = existingDatesInRange.includes(d);
                              const dayDate = new Date(d);
                              const dayName = dayDate.toLocaleDateString('pt-PT', { weekday: 'short' });
                              
                              return (
                                <button
                                  key={d}
                                  onClick={() => toggleDayRemoval(d)}
                                  className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${
                                    isRemoved 
                                      ? 'border-red-400/40 bg-red-500/10 text-red-400 line-through opacity-60' 
                                      : isExisting
                                        ? 'border-amber-400/40 bg-amber-500/10 text-amber-500'
                                        : `${t.card} ${t.text}`
                                  }`}
                                  title={isRemoved ? 'Clique para incluir este dia' : isExisting ? 'Dados já existem - clique para excluir' : 'Clique para excluir este dia'}
                                >
                                  <span className="font-medium">{dayDate.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' })}</span>
                                  <span className={`ml-1 ${t.textMut}`}>{dayName}</span>
                                  {isExisting && !isRemoved && <span className="ml-1 text-[10px]">(existe)</span>}
                                </button>
                              );
                            })}
                          </div>
                          <p className={`mt-2 text-xs ${t.textMut}`}>
                            Clique num dia para excluí-lo da importação
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Warning: Days mismatch */}
                {detectedDays > 0 && effectiveDateRange.length > 0 && detectedDays !== effectiveDateRange.length && (
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <div className="text-xs">
                      <span className="font-medium text-red-500">Erro:</span>
                      <span className={t.textBody}> Detectados <strong>{detectedDays}</strong> dias nos dados, mas <strong>{effectiveDateRange.length}</strong> dias seleccionados. </span>
                      <span className={t.textMut}>
                        {effectiveDateRange.length > detectedDays 
                          ? `Remova ${effectiveDateRange.length - detectedDays} dia(s) clicando nos dias acima.`
                          : `Adicione ${detectedDays - effectiveDateRange.length} dia(s) ao intervalo.`
                        }
                      </span>
                    </div>
                  </div>
                )}

                {/* Warning: Existing data */}
                {existingDatesInRange.filter(d => !daysToRemove.includes(d)).length > 0 && (
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <div className="text-xs">
                      <span className="font-medium text-amber-500">Atenção:</span>
                      <span className={t.textBody}> Já existem dados para {existingDatesInRange.filter(d => !daysToRemove.includes(d)).length} dia(s) seleccionado(s). </span>
                      <span className={t.textMut}>Os dados existentes serão substituídos.</span>
                    </div>
                  </div>
                )}

                {/* Success indicator */}
                {detectedDays > 0 && effectiveDateRange.length === detectedDays && (
                  <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    <span className={`text-xs text-emerald-600`}>
                      <strong>{detectedDays}</strong> dia{detectedDays > 1 ? 's' : ''} de operações pronto{detectedDays > 1 ? 's' : ''} para importar
                    </span>
                  </div>
                )}

                {/* Help text */}
                <p className={`text-xs ${t.textMut} text-center`}>
                  Selecione toda a tabela no software (Ctrl+A), copie (Ctrl+C) e cole aqui
                </p>
              </div>

              {/* Footer */}
              <div className={`px-6 py-4 ${t.inner} flex justify-end gap-3 sticky bottom-0`}>
                <button 
                  onClick={() => { setShowPasteModal(false); setPasteText(''); setDetectedDays(0); setParsedLines(0); setDaysToRemove([]); }}
                  className={`px-4 py-2 text-sm rounded-md ${t.card} ${t.textSec} hover:opacity-80 transition-opacity`}
                >
                  Cancelar
                </button>
                <button 
                  onClick={validateAndImport}
                  disabled={pasting || !pasteText.trim() || effectiveDateRange.length === 0 || detectedDays !== effectiveDateRange.length}
                  className="flex items-center gap-2 px-5 py-2 text-sm rounded-md bg-sky-500 text-white hover:bg-sky-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {pasting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      A processar...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Importar Dados
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Replace Confirmation Modal */}
        {showReplaceConfirm && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className={`${t.card} rounded-xl p-6 max-w-md w-full mx-4`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <h3 className={`text-base font-semibold ${t.text}`}>Dados Existentes</h3>
                  <p className={`text-xs ${t.textMut}`}>Já existem dados para alguns dias seleccionados</p>
                </div>
              </div>
              <div className="mb-5">
                <p className={`text-sm ${t.textBody} mb-3`}>
                  Os seguintes dias já têm dados importados:
                </p>
                <div className="flex flex-wrap gap-2">
                  {existingDatesInRange.filter(d => !daysToRemove.includes(d)).map(d => (
                    <span key={d} className="px-2 py-1 text-xs rounded-full bg-amber-500/10 text-amber-500 border border-amber-500/20">
                      {new Date(d).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button 
                  onClick={() => setShowReplaceConfirm(false)}
                  className={`px-4 py-2 text-sm rounded-md ${t.inner} ${t.textSec} hover:opacity-80 transition-opacity`}
                >
                  Cancelar
                </button>
                <button 
                  onClick={() => handlePasteData(true)}
                  className="px-4 py-2 text-sm rounded-md bg-amber-500 text-white hover:bg-amber-600 transition-colors"
                >
                  Substituir Dados
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Break Review Modal */}
        {showBreakReviewModal && previewData && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className={`${t.card} rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-violet-500/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-violet-500" />
                  </div>
                  <div>
                    <h3 className={`text-lg font-semibold ${t.text}`}>Pausas Legais Detectadas</h3>
                    <p className={`text-sm ${t.textMut}`}>
                      Revise as pausas detectadas antes de guardar
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    setShowBreakReviewModal(false);
                    setShowPasteModal(true);
                  }}
                  className={`p-2 rounded-lg ${t.inner} ${t.textMut} hover:${t.text} transition-colors`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Summary */}
              <div className={`${t.inner} rounded-lg p-4 mb-4`}>
                <div className="flex items-center gap-6 text-sm flex-wrap">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-sky-500" />
                    <span className={t.textMut}>Dias:</span>
                    <span className={`font-medium ${t.text}`}>{previewData.total_days}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Scissors className="w-4 h-4 text-emerald-500" />
                    <span className={t.textMut}>Registos:</span>
                    <span className={`font-medium ${t.text}`}>{previewData.total_records}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-violet-500" />
                    <span className={t.textMut}>Pausas:</span>
                    <span className={`font-medium ${t.text}`}>{breakDeductions.length}</span>
                  </div>
                  {previewData.has_overtime && (
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-amber-500" />
                      <span className="text-amber-500 font-medium">Turno Extra detectado</span>
                    </div>
                  )}
                </div>
                {previewData.days?.map(day => day.has_overtime_shift && (
                  <div key={day.date} className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-amber-500" />
                      <span className="text-sm text-amber-500 font-medium">
                        Turno Extra em {new Date(day.date).toLocaleDateString('pt-PT')}
                      </span>
                    </div>
                    <p className={`text-xs ${t.textMut} mt-1`}>
                      Período: {day.first_start} → {day.last_end} • As pausas não se aplicam ao turno extra (após 17:30)
                    </p>
                  </div>
                ))}
              </div>
              
              {/* Legend */}
              <div className={`text-xs ${t.textMut} mb-4 flex items-center gap-4`}>
                <span className="font-medium">LEGENDA:</span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-red-400"></span> Parada = Máquina Parada
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-cyan-400"></span> E.Marc = Entre Marcadas
                </span>
              </div>
              
              {/* Break deductions list */}
              <div className="space-y-3 mb-6">
                {breakDeductions.map((brk, idx) => {
                  // Get weekday with Portuguese names
                  const weekdayNames = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
                  const dateObj = brk.date ? new Date(brk.date) : null;
                  const weekday = dateObj ? weekdayNames[dateObj.getDay()] : '';
                  const dayMonth = dateObj ? dateObj.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' }) : '';
                  
                  // Weekday colors
                  const weekdayColors = {
                    'Segunda': 'bg-blue-500',
                    'Terça': 'bg-green-500',
                    'Quarta': 'bg-amber-500',
                    'Quinta': 'bg-purple-500',
                    'Sexta': 'bg-red-500',
                    'Sábado': 'bg-orange-500',
                    'Domingo': 'bg-gray-500'
                  };
                  
                  return (
                  <div 
                    key={idx} 
                    className={`${t.inner} rounded-lg p-4 border ${brk.enabled !== false ? 'border-violet-500/30' : 'border-gray-500/20 opacity-50'}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={brk.enabled !== false}
                          onChange={() => toggleBreakDeduction(idx)}
                          className="mt-1 w-4 h-4 rounded border-gray-500/30 text-violet-500 focus:ring-violet-500"
                        />
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            {brk.break_type === 'morning' && <Coffee className="w-4 h-4 text-amber-500" />}
                            {brk.break_type === 'lunch' && <Utensils className="w-4 h-4 text-orange-500" />}
                            {brk.break_type === 'afternoon' && <Coffee className="w-4 h-4 text-amber-500" />}
                            <span className={`font-medium ${t.text}`}>{brk.break_name}</span>
                            {brk.date && (
                              <div className="flex items-center gap-1">
                                <span className={`text-xs px-2 py-1 rounded-md ${weekdayColors[weekday] || 'bg-gray-500'} text-white font-bold uppercase tracking-wide`}>
                                  {weekday}
                                </span>
                                <span className={`text-xs px-2 py-1 rounded-md bg-slate-600 text-white font-mono`}>
                                  {dayMonth}
                                </span>
                              </div>
                            )}
                          </div>
                          <p className={`text-sm ${t.textMut}`}>
                            {brk.gap_info ? (
                              <>
                                <span className="text-cyan-400">Gap:</span> <span className="font-mono">{brk.gap_info.replace('Gap: ', '')}</span>
                              </>
                            ) : (
                              <>
                                Registo: <span className="font-mono">{brk.start_time}</span> → <span className="font-mono">{brk.end_time}</span>
                              </>
                            )}
                          </p>
                          
                          {/* Visual comparison bars */}
                          <div className="mt-2 space-y-1.5">
                            {/* Parada bar */}
                            <div className="flex items-center gap-2">
                              <div 
                                className={`h-5 flex items-center px-2 rounded transition-all cursor-pointer ${
                                  brk.deduct_from === 'machine_stopped' 
                                    ? 'bg-red-500 text-white ring-2 ring-red-300' 
                                    : 'bg-red-500/30 text-red-400 hover:bg-red-500/50'
                                }`}
                                style={{ 
                                  width: `${Math.max(40, Math.min(150, (brk.record_machine_stopped || 0) * 5))}px`
                                }}
                                onClick={() => changeDeductFrom(idx, 'machine_stopped')}
                                title="Clique para descontar de Parada"
                              >
                                <span className="text-[10px] font-bold whitespace-nowrap">
                                  Parada: {(brk.record_machine_stopped || 0).toFixed(1)}m
                                </span>
                              </div>
                              {brk.deduct_from === 'machine_stopped' && (
                                <span className="text-[10px] text-red-400 font-bold">← FONTE</span>
                              )}
                            </div>
                            
                            {/* Entre Marcadas bar */}
                            <div className="flex items-center gap-2">
                              <div 
                                className={`h-5 flex items-center px-2 rounded transition-all cursor-pointer ${
                                  brk.deduct_from === 'between_markers' 
                                    ? 'bg-cyan-500 text-white ring-2 ring-cyan-300' 
                                    : 'bg-cyan-500/30 text-cyan-400 hover:bg-cyan-500/50'
                                }`}
                                style={{ 
                                  width: `${Math.max(40, Math.min(150, (brk.record_between_markers || 0) * 5))}px`
                                }}
                                onClick={() => changeDeductFrom(idx, 'between_markers')}
                                title="Clique para descontar de Entre Marcadas"
                              >
                                <span className="text-[10px] font-bold whitespace-nowrap">
                                  E.Marc: {(brk.record_between_markers || 0).toFixed(1)}m
                                </span>
                              </div>
                              {brk.deduct_from === 'between_markers' && (
                                <span className="text-[10px] text-cyan-400 font-bold">← FONTE</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-col items-end gap-2">
                        <span className="text-xl font-bold text-violet-500">
                          -{brk.minutes_to_deduct} min
                        </span>
                        <span className={`text-[10px] ${t.textMut}`}>
                          a descontar
                        </span>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
              
              {/* Totals */}
              <div className={`${t.inner} rounded-lg p-4 mb-4`}>
                <div className="flex items-center justify-between">
                  <span className={`font-medium ${t.text}`}>Total a descontar:</span>
                  <div className="flex items-center gap-4">
                    <span className="text-sm">
                      <span className={t.textMut}>Parada:</span>{' '}
                      <span className="text-red-400 font-bold">
                        -{breakDeductions.filter(d => d.enabled !== false && d.deduct_from === 'machine_stopped').reduce((sum, d) => sum + d.minutes_to_deduct, 0).toFixed(1)} min
                      </span>
                    </span>
                    <span className="text-sm">
                      <span className={t.textMut}>E.Marc:</span>{' '}
                      <span className="text-cyan-400 font-bold">
                        -{breakDeductions.filter(d => d.enabled !== false && d.deduct_from === 'between_markers').reduce((sum, d) => sum + d.minutes_to_deduct, 0).toFixed(1)} min
                      </span>
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowBreakReviewModal(false);
                    setShowPasteModal(true);
                  }}
                  className={`px-4 py-2 rounded-md ${t.inner} ${t.textSec} hover:opacity-80`}
                >
                  Voltar
                </button>
                <button
                  onClick={() => confirmPasteData(previewReplace, [])}
                  className={`px-4 py-2 rounded-md ${t.inner} ${t.textMut} hover:opacity-80`}
                >
                  Guardar sem descontar
                </button>
                <button
                  onClick={() => confirmPasteData(previewReplace, breakDeductions)}
                  disabled={pasting}
                  className="px-4 py-2 rounded-md bg-violet-500 text-white hover:bg-violet-600 disabled:opacity-50 flex items-center gap-2"
                >
                  {pasting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      A guardar...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Aplicar e Guardar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Anomalous Records Review Modal (Step 2 of Import) */}
        {showAnomalousReviewModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className={`${t.card} rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto`}>
              <div className="p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-500/20">
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                    </div>
                    <div>
                      <h3 className={`text-lg font-bold ${t.text}`}>Etapa 2: Classificar Tempos Anormais</h3>
                      <p className={`text-sm ${t.textMut}`}>{anomalousRecords.length} registo(s) com tempo &gt;20 min detectados</p>
                    </div>
                  </div>
                </div>
                
                {/* Info Box */}
                <div className={`${t.inner} rounded-lg p-4 mb-6 border-l-4 border-amber-500`}>
                  <p className={`text-sm ${t.text}`}>
                    Seleccione um <strong>motivo</strong> e especifique quantos <strong>minutos extrair</strong> dos cálculos.
                  </p>
                  <p className={`text-xs ${t.textMut} mt-1`}>
                    Os minutos extraídos serão removidos da eficiência. Configure os motivos nas Configurações.
                  </p>
                </div>
                
                {/* Records List */}
                <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
                  {anomalousRecords.map((record) => {
                    const key = `${record.date}_${record.record_index}_${record.field_type}`;
                    const classification = anomalousClassifications[key];
                    const reasons = getAnomalousReasons();
                    
                    return (
                      <div 
                        key={key}
                        className={`p-4 rounded-lg border ${t.inner} ${
                          classification?.reason ? 'border-emerald-500/30' : 'border-amber-500/50'
                        }`}
                      >
                        {/* Record Header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs ${t.textMut}`}>{record.date}</span>
                            {record.is_saturday && (
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-violet-500/10 text-violet-500">
                                Sábado
                              </span>
                            )}
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              record.field_type === 'machine_stopped' 
                                ? 'bg-red-500/10 text-red-500' 
                                : 'bg-amber-500/10 text-amber-500'
                            }`}>
                              {record.field_type === 'machine_stopped' ? 'Máq. Parada' : 'Entre Marcadas'}
                            </span>
                            <span className={`text-lg font-bold ${t.text}`}>
                              {record.original_value.toFixed(1)} min
                            </span>
                            <span className={`text-xs ${t.textMut}`}>
                              ({record.start_time} → {record.end_time})
                            </span>
                          </div>
                          {classification?.reason && (
                            <div className="flex items-center gap-1 text-emerald-500">
                              <Check className="w-4 h-4" />
                              <span className="text-xs font-medium">Classificado</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Classification Type Selection */}
                        <div className="mb-3">
                          <label className={`text-xs ${t.textMut} block mb-2`}>Classificar como:</label>
                          <div className="flex gap-2 mb-3">
                            <button
                              onClick={() => {
                                const newC = { ...anomalousClassifications };
                                newC[key] = { ...(newC[key] || {}), classify_as: 'machine_stopped' };
                                setAnomalousClassifications(newC);
                              }}
                              className={`px-3 py-1.5 text-xs rounded-lg flex items-center gap-1.5 transition-all ${
                                classification?.classify_as === 'machine_stopped'
                                  ? 'bg-red-500/20 text-red-500 ring-2 ring-red-500/50 font-medium'
                                  : `${t.card} ${t.textMut} hover:opacity-80`
                              }`}
                            >
                              Máq. Parada
                            </button>
                            <button
                              onClick={() => {
                                const newC = { ...anomalousClassifications };
                                newC[key] = { ...(newC[key] || {}), classify_as: 'between_markers' };
                                setAnomalousClassifications(newC);
                              }}
                              className={`px-3 py-1.5 text-xs rounded-lg flex items-center gap-1.5 transition-all ${
                                classification?.classify_as === 'between_markers'
                                  ? 'bg-amber-500/20 text-amber-500 ring-2 ring-amber-500/50 font-medium'
                                  : `${t.card} ${t.textMut} hover:opacity-80`
                              }`}
                            >
                              Entre Marcadas
                            </button>
                            <button
                              onClick={() => {
                                const newC = { ...anomalousClassifications };
                                newC[key] = { ...(newC[key] || {}), classify_as: 'exclude' };
                                setAnomalousClassifications(newC);
                              }}
                              className={`px-3 py-1.5 text-xs rounded-lg flex items-center gap-1.5 transition-all ${
                                classification?.classify_as === 'exclude'
                                  ? 'bg-violet-500/20 text-violet-500 ring-2 ring-violet-500/50 font-medium'
                                  : `${t.card} ${t.textMut} hover:opacity-80`
                              }`}
                            >
                              Excluir (Pausa)
                            </button>
                          </div>
                        </div>

                        {/* Reason Selection */}
                        <div className="mb-3">
                          <label className={`text-xs ${t.textMut} block mb-2`}>Motivo:</label>
                          <div className="flex flex-wrap gap-2">
                            {reasons.map(reason => (
                              <button
                                key={reason.id}
                                onClick={() => handleAnomalousClassification(record, reason.id, record.original_value)}
                                className={`px-3 py-1.5 text-xs rounded-lg flex items-center gap-1.5 transition-all ${
                                  classification?.reason === reason.id
                                    ? 'ring-2 ring-offset-1 font-medium'
                                    : `${t.card} hover:opacity-80`
                                }`}
                                style={{
                                  backgroundColor: classification?.reason === reason.id ? reason.color + '30' : '',
                                  color: classification?.reason === reason.id ? reason.color : '',
                                  borderColor: classification?.reason === reason.id ? reason.color : ''
                                }}
                              >
                                <span>{reason.icon}</span>
                                <span>{reason.name}</span>
                              </button>
                            ))}
                            <button
                              onClick={() => {
                                // Clear classification
                                const newClassifications = { ...anomalousClassifications };
                                delete newClassifications[key];
                                setAnomalousClassifications(newClassifications);
                              }}
                              className={`px-3 py-1.5 text-xs rounded-lg ${t.card} ${t.textMut} hover:text-red-500`}
                            >
                              Limpar
                            </button>
                          </div>
                        </div>
                        
                        {/* Minutes to Extract - Only shown when reason is selected */}
                        {classification?.reason && (
                          <div className={`pt-3 border-t ${t.divider}`}>
                            <div className="flex items-center gap-4">
                              <label className={`text-xs ${t.textMut}`}>Minutos a extrair:</label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="range"
                                  min="0"
                                  max={record.original_value}
                                  step="0.5"
                                  value={classification.minutes_to_extract || record.original_value}
                                  onChange={(e) => updateMinutesToExtract(record, parseFloat(e.target.value))}
                                  className="w-32 h-2 rounded-lg appearance-none cursor-pointer accent-amber-500"
                                />
                                <input
                                  type="number"
                                  min="0"
                                  max={record.original_value}
                                  step="0.5"
                                  value={classification.minutes_to_extract || record.original_value}
                                  onChange={(e) => updateMinutesToExtract(record, parseFloat(e.target.value) || 0)}
                                  className={`w-20 px-2 py-1 rounded text-sm text-center ${t.inputBg} ${t.card} ${t.text}`}
                                />
                                <span className={`text-sm ${t.textMut}`}>/ {record.original_value.toFixed(1)} min</span>
                              </div>
                            </div>
                            <p className={`text-xs ${t.textMut} mt-2`}>
                              Serão extraídos <strong className="text-amber-500">{(classification.minutes_to_extract || record.original_value).toFixed(1)} min</strong> como "{reasons.find(r => r.id === classification.reason)?.name}"
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Summary */}
                <div className={`${t.inner} rounded-lg p-4 mb-4`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm ${t.text}`}>Classificados:</span>
                    <span className={`text-sm font-medium ${
                      Object.keys(anomalousClassifications).length === anomalousRecords.length 
                        ? 'text-emerald-500' 
                        : 'text-amber-500'
                    }`}>
                      {Object.keys(anomalousClassifications).length} / {anomalousRecords.length}
                    </span>
                  </div>
                  {Object.keys(anomalousClassifications).length > 0 && (
                    <div className={`text-xs ${t.textMut}`}>
                      Total a extrair: <strong className="text-amber-500">
                        {Object.values(anomalousClassifications).reduce((sum, c) => sum + (c.minutes_to_extract || 0), 0).toFixed(1)} min
                      </strong>
                    </div>
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex justify-end gap-3">
                  <button
                    onClick={skipAnomalousClassification}
                    className={`px-4 py-2 rounded-md ${t.inner} ${t.textMut} hover:opacity-80`}
                  >
                    Classificar Depois
                  </button>
                  <button
                    onClick={confirmAnomalousClassifications}
                    disabled={pasting}
                    className="px-4 py-2 rounded-md bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 flex items-center gap-2"
                  >
                    {pasting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        A processar...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        Confirmar e Finalizar
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64" data-testid="loading-state">
            <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !hasData ? (
          <EmptyState onPaste={() => openPasteModal()} t={t} />
        ) : (
          <>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 mb-6">
              <TabsList className={`inline-flex justify-start gap-1 ${t.card} p-1 rounded-lg w-max min-w-full`} data-testid="main-tabs">
                <TabsTrigger value="dashboard" className="flex items-center gap-2 whitespace-nowrap data-[state=active]:bg-sky-500 data-[state=active]:text-white">
                  <BarChart3 className="w-4 h-4" />
                  Dashboard
                </TabsTrigger>
                <TabsTrigger value="daily" className="flex items-center gap-2 whitespace-nowrap data-[state=active]:bg-sky-500 data-[state=active]:text-white">
                  <Table className="w-4 h-4" />
                  Análise Diária
                </TabsTrigger>
                <TabsTrigger value="imports" className="flex items-center gap-2 whitespace-nowrap data-[state=active]:bg-sky-500 data-[state=active]:text-white">
                  <Calendar className="w-4 h-4" />
                  Importações
                </TabsTrigger>
                <TabsTrigger value="simulator" className="flex items-center gap-2 whitespace-nowrap data-[state=active]:bg-violet-500 data-[state=active]:text-white">
                  <Sliders className="w-4 h-4" />
                  Simulador & Metas
                </TabsTrigger>
                <TabsTrigger value="anomalous" className="flex items-center gap-2 whitespace-nowrap data-[state=active]:bg-red-500 data-[state=active]:text-white">
                  <AlertTriangle className="w-4 h-4" />
                  Registos Anormais
                </TabsTrigger>
                <TabsTrigger value="settings" className="flex items-center gap-2 whitespace-nowrap data-[state=active]:bg-sky-500 data-[state=active]:text-white">
                  <Settings className="w-4 h-4" />
                  Configurações
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Dashboard Tab */}
            <TabsContent value="dashboard" className="space-y-6 mt-0">
              {/* Period Selector */}
              <div className={`${t.card} rounded-lg p-4`}>
                <div className="flex flex-wrap items-center gap-3">
                  <span className={`text-sm font-semibold ${t.text}`}>Período:</span>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setDashboardPeriod('all')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        dashboardPeriod === 'all' 
                          ? 'bg-sky-500 text-white' 
                          : `${t.inner} ${t.textSec} hover:opacity-80`
                      }`}
                    >
                      Todos
                    </button>
                    <button
                      onClick={() => setDashboardPeriod('today')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        dashboardPeriod === 'today' 
                          ? 'bg-emerald-500 text-white' 
                          : `${t.inner} ${t.textSec} hover:opacity-80`
                      }`}
                    >
                      Hoje
                    </button>
                    <button
                      onClick={() => setDashboardPeriod('week')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        dashboardPeriod === 'week' 
                          ? 'bg-amber-500 text-white' 
                          : `${t.inner} ${t.textSec} hover:opacity-80`
                      }`}
                    >
                      Última Semana
                    </button>
                    <button
                      onClick={() => setDashboardPeriod('month')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        dashboardPeriod === 'month' 
                          ? 'bg-violet-500 text-white' 
                          : `${t.inner} ${t.textSec} hover:opacity-80`
                      }`}
                    >
                      Último Mês
                    </button>
                    <button
                      onClick={() => setDashboardPeriod('custom')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        dashboardPeriod === 'custom' 
                          ? 'bg-red-500 text-white' 
                          : `${t.inner} ${t.textSec} hover:opacity-80`
                      }`}
                    >
                      Personalizado
                    </button>
                  </div>
                  
                  {dashboardPeriod === 'custom' && (
                    <div className="flex items-center gap-2 ml-auto">
                      <Input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className={`w-36 ${t.input}`}
                      />
                      <span className={t.textMut}>→</span>
                      <Input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className={`w-36 ${t.input}`}
                      />
                    </div>
                  )}
                  
                  {/* Period info badge */}
                  <div className={`ml-auto px-3 py-1 rounded-full text-xs font-semibold ${t.inner} ${t.textMut}`}>
                    {dashboardPeriod === 'all' 
                      ? `${summary?.daily_summary?.length || 0} dia(s) | ${summary?.total_cuts || 0} cortes`
                      : `${filteredSummary?.total_days || 0} dia(s) | ${filteredSummary?.total_cuts || 0} cortes`
                    }
                  </div>
                </div>
              </div>
              
              <KPICards summary={filteredSummary} settings={settings} t={t} />
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <TimeDistribution summary={filteredSummary} t={t} />
                </div>
                <div>
                  <CompositionChart summary={filteredSummary} t={t} />
                </div>
              </div>
              <MonthlyEvolution dailySummary={filteredSummary?.daily_summary} t={t} />
            </TabsContent>

            {/* Daily Analysis Tab */}
            <TabsContent value="daily" className="space-y-6 mt-0">
              <DailyAnalysisView 
                dailySummary={summary.daily_summary} 
                selectedDay={dayDetail}
                onSelectDay={(day) => {
                  if (day && day.date) {
                    setSelectedDay(day.date);
                  } else if (typeof day === 'string') {
                    setSelectedDay(day);
                  } else {
                    setSelectedDay(null);
                  }
                }}
                onDeleteDay={handleDeleteDay}
                onRefresh={() => { fetchSummary(); if (selectedDay) fetchDayDetail(selectedDay); }}
                settings={settings}
                t={t}
              />
            </TabsContent>

            {/* Simulator Tab */}
            <TabsContent value="simulator" className="space-y-6 mt-0">
              <Simulator 
                summary={summary} 
                settings={settings} 
                simEntreCortes={simEntreCortes}
                setSimEntreCortes={setSimEntreCortes}
                simulatedMetrics={simulatedMetrics}
                t={t}
                // Goals management props
                goals={goals}
                loadingGoals={loadingGoals}
                onCreateGoal={() => setShowCreateGoalModal(true)}
                onToggleActive={toggleGoalActive}
                onDeleteGoal={deleteGoal}
                onRefreshGoal={refreshGoal}
                onEditGoal={openEditGoal}
                onGeneratePDF={generateGoalPDF}
                selectedGoalForChart={selectedGoalForChart}
                setSelectedGoalForChart={setSelectedGoalForChart}
              />
            </TabsContent>

            {/* Imports History Tab */}
            <TabsContent value="imports" className="space-y-6 mt-0">
              <ImportsHistory 
                importHistory={importHistory}
                onDeleteDay={handleDeleteDay}
                onRefresh={() => { fetchSummary(); fetchImportHistory(); }}
                t={t}
              />
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings" className="space-y-6 mt-0">
              <SettingsPanel settings={settings} onSave={saveSettings} t={t} />
            </TabsContent>

            {/* Anomalous Records Tab */}
            <TabsContent value="anomalous" className="space-y-6 mt-0">
              <AnomalousRecordsPanel 
                t={t}
                settings={settings}
                onRecordsClassified={() => fetchSummary()}
              />
            </TabsContent>
          </Tabs>

          {/* Create Goal Modal */}
          {showCreateGoalModal && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
              <div className={`${t.card} rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto`}>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-violet-500/20">
                        <Target className="w-5 h-5 text-violet-500" />
                      </div>
                      <div>
                        <h3 className={`text-lg font-bold ${t.text}`}>Nova Meta</h3>
                        <p className={`text-sm ${t.textMut}`}>Crie uma simulação baseada num período</p>
                      </div>
                    </div>
                    <button onClick={() => setShowCreateGoalModal(false)} className={`p-2 rounded-lg ${t.hover}`}>
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    {/* Goal Name */}
                    <div>
                      <label className={`text-sm font-medium ${t.text} block mb-1.5`}>Nome da Meta</label>
                      <Input
                        value={newGoal.name}
                        onChange={(e) => setNewGoal(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Ex: Meta Q1 2025, Objetivo Março..."
                        className={`${t.input}`}
                      />
                    </div>

                    {/* Period Selection */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={`text-sm font-medium ${t.text} block mb-1.5`}>Data Início</label>
                        <Input
                          type="date"
                          value={newGoal.periodStart}
                          onChange={(e) => setNewGoal(prev => ({ ...prev, periodStart: e.target.value }))}
                          className={`${t.input}`}
                        />
                      </div>
                      <div>
                        <label className={`text-sm font-medium ${t.text} block mb-1.5`}>Data Fim</label>
                        <Input
                          type="date"
                          value={newGoal.periodEnd}
                          onChange={(e) => setNewGoal(prev => ({ ...prev, periodEnd: e.target.value }))}
                          className={`${t.input}`}
                        />
                      </div>
                    </div>

                    {/* Target Parameters - Primary Configuration */}
                    <div className={`${t.inner} rounded-lg p-4 space-y-4`}>
                      <h4 className={`text-sm font-semibold ${t.text} flex items-center gap-2`}>
                        <Target className="w-4 h-4 text-emerald-500" />
                        Metas Alvo
                      </h4>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className={`text-sm ${t.textMut} block mb-1.5`}>
                            Tempo Entre Marcadas (≤)
                          </label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="0"
                              max="30"
                              step="0.1"
                              value={newGoal.targetBetweenMarkersTime}
                              onChange={(e) => setNewGoal(prev => ({ ...prev, targetBetweenMarkersTime: parseFloat(e.target.value) || 0 }))}
                              className={`${t.input} w-24 text-center`}
                            />
                            <span className={`text-sm ${t.textMut}`}>min</span>
                          </div>
                          <p className={`text-xs ${t.textMut} mt-1`}>Tempo máximo entre cortes</p>
                        </div>

                        <div>
                          <label className={`text-sm ${t.textMut} block mb-1.5`}>
                            % Parada / Tempo Corte (≤)
                          </label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="0"
                              max="200"
                              step="0.1"
                              value={newGoal.targetMachineStoppedPercent}
                              onChange={(e) => setNewGoal(prev => ({ ...prev, targetMachineStoppedPercent: parseFloat(e.target.value) || 0 }))}
                              className={`${t.input} w-24 text-center`}
                            />
                            <span className={`text-sm ${t.textMut}`}>%</span>
                          </div>
                          <p className={`text-xs ${t.textMut} mt-1`}>Paragem máx. em relação ao corte</p>
                        </div>
                      </div>
                      
                      <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                        <div>
                          <label className={`text-sm ${t.textMut} block mb-1.5`}>
                            Eficiência Mínima (≥)
                          </label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="1"
                              value={newGoal.targetEfficiency}
                              onChange={(e) => setNewGoal(prev => ({ ...prev, targetEfficiency: parseInt(e.target.value) || 0 }))}
                              className={`${t.input} w-24 text-center`}
                            />
                            <span className={`text-sm ${t.textMut}`}>%</span>
                          </div>
                          <p className={`text-xs ${t.textMut} mt-1`}>Corte ÷ (Corte + Intra Marc.) × 100</p>
                        </div>
                      </div>
                    </div>

                    {/* Info Box */}
                    <div className={`${t.inner} rounded-lg p-3 border border-sky-500/30`}>
                      <p className={`text-xs ${t.textMut}`}>
                        <AlertCircle className="w-3.5 h-3.5 inline mr-1 text-sky-500" />
                        A meta será calculada com base nos dados reais do período selecionado. 
                        Configure os limites máximos para tempo entre marcadas e % de paragem em relação ao tempo de corte.
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    <Button variant="outline" onClick={() => setShowCreateGoalModal(false)} className={t.buttonOutline}>
                      Cancelar
                    </Button>
                    <Button 
                      onClick={createGoal} 
                      disabled={creatingGoal}
                      className="bg-violet-500 hover:bg-violet-600 text-white"
                    >
                      {creatingGoal ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          A criar...
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-2" />
                          Criar Meta
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Edit Goal Modal */}
          {editingGoal && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
              <div className={`${t.card} rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto`}>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-blue-500/20">
                        <Settings className="w-5 h-5 text-blue-500" />
                      </div>
                      <div>
                        <h3 className={`text-lg font-bold ${t.text}`}>Editar Meta</h3>
                        <p className={`text-sm ${t.textMut}`}>Ajuste os parâmetros da simulação</p>
                      </div>
                    </div>
                    <button onClick={() => setEditingGoal(null)} className={`p-2 rounded-lg ${t.hover}`}>
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    {/* Goal Name */}
                    <div>
                      <label className={`text-sm font-medium ${t.text} block mb-1.5`}>Nome da Meta</label>
                      <Input
                        value={editingGoal.name}
                        onChange={(e) => setEditingGoal(prev => ({ ...prev, name: e.target.value }))}
                        className={`${t.input}`}
                      />
                    </div>

                    {/* Period Info (read-only) */}
                    <div className={`${t.inner} rounded-lg p-3`}>
                      <p className={`text-sm ${t.textMut}`}>
                        <Calendar className="w-4 h-4 inline mr-1" />
                        Período: {editingGoal.period_start} → {editingGoal.period_end}
                      </p>
                    </div>

                    {/* Reduction Parameters */}
                    <div className={`${t.inner} rounded-lg p-4 space-y-4`}>
                      <h4 className={`text-sm font-semibold ${t.text}`}>Parâmetros de Simulação</h4>
                      
                      <div>
                        <div className="flex justify-between mb-1">
                          <label className={`text-sm ${t.textMut}`}>Redução Entre Marcadas</label>
                          <span className="text-sm font-bold text-cyan-500">{editingGoal.betweenMarkersReduction}%</span>
                        </div>
                        <input
                          type="range"
                          min="0" max="80" step="5"
                          value={editingGoal.betweenMarkersReduction}
                          onChange={(e) => setEditingGoal(prev => ({ ...prev, betweenMarkersReduction: parseInt(e.target.value) }))}
                          className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                        />
                      </div>

                      <div>
                        <div className="flex justify-between mb-1">
                          <label className={`text-sm ${t.textMut}`}>Redução Máquina Parada</label>
                          <span className="text-sm font-bold text-red-500">{editingGoal.machineStoppedReduction}%</span>
                        </div>
                        <input
                          type="range"
                          min="0" max="80" step="5"
                          value={editingGoal.machineStoppedReduction}
                          onChange={(e) => setEditingGoal(prev => ({ ...prev, machineStoppedReduction: parseInt(e.target.value) }))}
                          className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-red-500"
                        />
                      </div>
                    </div>

                    {/* Target Parameters */}
                    <div className={`${t.inner} rounded-lg p-4 space-y-4`}>
                      <h4 className={`text-sm font-semibold ${t.text}`}>Metas Alvo</h4>
                      
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className={`text-sm ${t.textMut} block mb-1.5`}>E.Marc (≤)</label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number" min="0" max="30" step="0.1"
                              value={editingGoal.targetBetweenMarkersTime}
                              onChange={(e) => setEditingGoal(prev => ({ ...prev, targetBetweenMarkersTime: parseFloat(e.target.value) || 0 }))}
                              className={`${t.input} w-16 text-center`}
                            />
                            <span className={`text-xs ${t.textMut}`}>min</span>
                          </div>
                        </div>
                        
                        <div>
                          <label className={`text-sm ${t.textMut} block mb-1.5`}>Eficiência (≥)</label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number" min="0" max="100" step="1"
                              value={editingGoal.targetEfficiency}
                              onChange={(e) => setEditingGoal(prev => ({ ...prev, targetEfficiency: parseInt(e.target.value) || 0 }))}
                              className={`${t.input} w-16 text-center`}
                            />
                            <span className={`text-xs ${t.textMut}`}>%</span>
                          </div>
                        </div>

                        <div>
                          <label className={`text-sm ${t.textMut} block mb-1.5`}>% Parada (≤)</label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number" min="0" max="200" step="0.1"
                              value={editingGoal.targetMachineStoppedPercent}
                              onChange={(e) => setEditingGoal(prev => ({ ...prev, targetMachineStoppedPercent: parseFloat(e.target.value) || 0 }))}
                              className={`${t.input} w-16 text-center`}
                            />
                            <span className={`text-xs ${t.textMut}`}>%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    <Button variant="outline" onClick={() => setEditingGoal(null)} className={t.buttonOutline}>
                      Cancelar
                    </Button>
                    <Button onClick={saveEditGoal} className="bg-blue-500 hover:bg-blue-600 text-white">
                      Guardar Alterações
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
        )}
      </div>
    </div>
  );
}



// Components GoalCard, DailyAnalysisView, DayDetail, ImportsHistory, Simulator
// are now imported from ./cutting-simulator/components


// SettingsPanel component is now imported from ./cutting-simulator/components

// Main Export with Theme Provider
export default function CuttingSimulator() {
  return (
    <MainLayout title="Máquina de Corte">
      <CuttingThemeProvider>
        <CuttingSimulatorContent />
      </CuttingThemeProvider>
    </MainLayout>
  );
}
